import { defineStore } from 'pinia';
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization';
import { useRootStore } from '~/store/root';
import { localStorageService } from '~/libs/local-storage.service'
import env from '~/libs/env'
import appConstants from '~/libs/constants/application-constants.js'
import { redirectToWidget } from '~/composables/identityWidgetLoginUrl'

const { nuxtBaseUrl } = appConstants

function storeReset(stores) {
  if (!window || !stores) {
    return
  }

  for (const name in stores) {
    const store = stores[name]

    if (!store || typeof store.reset !== 'function') {
      continue
    }

    try {
      store.reset()
    } catch (err) {
      log.error(`Failed to reset ${name} store`, err)
    }
  }
}

export const useIdentityStore = defineStore('identity', () => {
  const nuxtApp = useNuxtApp()
  const { $axios, $lendioCookies } = nuxtApp
  const rootStore = useRootStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const identityBaseUrl = env('identityServiceUrl')
  const updatePasswordApiUrl = `${identityBaseUrl}/identity/update-password`

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/

  const businessSelectUrl = ref('/identity/business-select')
  const iframeOnly = ref(null)
  const existingEmail = ref({email: '', exists: false, passwordSet: false})
  const clientIdentityProviders = ref([])
  const identityProvider = ref(null)
  const ssoLoginUrl = ref('/identity/sso')
  const passkeys = ref([])

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */

  const onlySsoSupported = computed(() => {
    return clientIdentityProviders.value?.length && clientIdentityProviders.value.every(idp => idp.authType === 'samlSso')
  })

  const externallyManagedIdentity = computed(() => {
    return identityProvider.value?.authType !== 'internalOauth'
  })

  const mustSetPassword = computed(() => {
    return !existingEmail.value?.passwordSet && !externallyManagedIdentity.value
  })

  const relayState = computed(() => nuxtBaseUrl + ssoLoginUrl.value)

  // Identity routes that are part of a post-login flow and should NOT redirect
  // already-authenticated users away to the app. Add to this list whenever a new
  // identity page needs to be reachable after a JWT has been issued.
  const postLoginIdentityUrls = computed(() => [
    businessSelectUrl.value,
  ])

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  /**
   * Exchanges a one-time login token for a real client token, without navigating.
   * @returns {Promise<void>}
   */
  async function _exchangeToken(token) {
    const exchangeTokenResponse = await $axios.get(`${identityBaseUrl}/auth/token-login`, {
      headers: { Authorization: `lendio-jwt ${token}` }
    })
    await rootStore.setAuthToken(exchangeTokenResponse.data.data)
    await rootStore.getCurrentUser({ forceReload: true, login: true })
  }

  /**
   * Exchange a one-time use login token for a client token
   * @returns {Promise<void>}
   */
  async function tokenLogin(token) {
    await _exchangeToken(token)
    await nuxtApp.runWithContext(() => navigateTo(rootStore.getRedirectUrl()))
  }

  /**
   * Exchanges the widget's one-time login token -- doesn't navigate, since the widget already redirected here.
   * @returns {Promise<void>}
   */
  async function consumeWidgetToken(token) {
    await _exchangeToken(token)
  }

  /**
   * Logout the user
   * @param {string|null} path - Optional path to redirect to after logout
   * @returns {Promise<void>}
   */
  async function logout(path = null) {
    await $axios.post(`${identityBaseUrl}/auth/logout`).catch((error) => {
      if (error?.response?.status !== 403) {
        log.warning('User Logout Errored', { error })
      }
    })
    await _clearCookies()
    _clearLocalStorage()
    _shutdownIntercom()
    _shutdownPusher()
    storeReset(nuxtApp.$store)
    rootStore.reset()

    if (!path) {
      redirectToWidget()
      return
    }

    return nuxtApp.runWithContext(() =>
      navigateTo(path, { external: true, replace: true }),
    )
  }

  /**
   *
   * @param oldPassword
   * @param newPassword
   * @returns {Promise<*>}
   */
  async function updatePassword(newPassword, oldPassword = null) {
    try {
      const res = await $axios.put(`${updatePasswordApiUrl}`, {
        oldPassword,
        newPassword,
      })
      existingEmail.value.passwordSet = true

      return res
    } catch (error) {
      const rawError = error?.response?.data?.errors?.[0];
      const responseErrorMessage = typeof rawError === 'string' ? rawError : rawError?.message;
      if (responseErrorMessage?.includes(('Unauthorized'))) {
        throw Object.assign(new Error('Old Password is not correct'), { isUserError: true })
      } else if (responseErrorMessage?.includes(('Password must '))) {
        throw Object.assign(new Error(responseErrorMessage), { isUserError: true })
      }
      throw new Error('We\'re sorry! Something went wrong.')
    }
  }

  async function isIframeOnly() {
    if (!!iframeOnly.value) return iframeOnly.value

    const response = await $axios.get(`${identityBaseUrl}/client/iframe-only`);
    iframeOnly.value = response.data.data !== false

    return iframeOnly.value
  }

  async function checkForSetPassword() {
    if (rootStore.isLoggedIn) {
      const authUser = rootStore.authUser
      if (typeof authUser?.passwordSet === 'boolean') {
        existingEmail.value = { email: authUser.email, exists: true, passwordSet: authUser.passwordSet }
      } else {
        try {
          await this.checkForExistingEmail(null, true);
        } catch (error) {
          log.warning('[IDENT] checkForSetPassword failed', { error })
          existingEmail.value.passwordSet = true
        }
      }
    }

    return existingEmail.value?.passwordSet;
  }

  async function checkForExistingEmail(email = null, force = false) {
    const authUser = rootStore.authUser
    const tenantId = affiliateCustomizationStore.tenantId
    email = authUser?.email ?? email

    if (existingEmail.value.email !== email || force) {
      const response = await $axios.post(
        `${env('apiUrl')}/borrower-portal/identity/exists`,
        { email, tenantId }
      )
      const data = response.data.data
      existingEmail.value = {
        email,
        exists: data.exists,
        passwordSet: data.passwordSet,
      }
    }

    return existingEmail.value
  }

  async function getSupportedIdentityProvidersForClient() {
    try {
      const response = await $axios.get(`${identityBaseUrl}/client/idp`)
      clientIdentityProviders.value = response.data.data
      return clientIdentityProviders.value
    } catch (error) {
      console.error('Error fetching identity providers:', error)
      clientIdentityProviders.value = []
    }
  }

  async function getAuthUserIdP() {
    const { authUser } = rootStore

    if (!authUser) {
      identityProvider.value = null;
    } else {
      const response = await $axios.post(`${identityBaseUrl}/auth/idp`, {
        identifier: authUser.email,
        tenantId: authUser.tenantId
      })

      identityProvider.value = response.data.data
    }

    return identityProvider.value
  }

  async function initiateSamlSsoLogin(identifier = null) {
    try {
      if (rootStore.isLoggedIn) {
        return
      }

      const tenantId = affiliateCustomizationStore.tenantId
      let body = { relayState: relayState.value }

      if (identifier && tenantId) {
        body = { ...body, identifier, tenantId }
      }

      const response = await $axios.post(`${identityBaseUrl}/auth/sso/sp`, body)
      const redirect = response.data?.data?.redirectUrl

      if (!redirect) {
        throw new Error('An error occurred. Please try again.')
      }

      window.location.href = redirect;
    } catch (error) {
      throw error
    }
  }

  /**
   * Get passkeys registered to the current identity
   * @returns {Promise<object[]>}
   */
  async function getPasskeys() {
    if (!usePasskeyEnabled()) {
      return []
    }

    const response = await $axios.get(`${identityBaseUrl}/identity/passkey`)
    passkeys.value = response.data.data || []
    return passkeys.value
  }

  async function getPasskeyRegisterOptions() {
    const response = await $axios.post(`${identityBaseUrl}/identity/passkey/register/options`)
    return response.data.data
  }

  async function verifyPasskeyRegistration(credential, friendlyName = null) {
    const response = await $axios.post(
      `${identityBaseUrl}/identity/passkey/register/verify`,
      { credential, friendlyName },
    )
    return response.data.data
  }

  async function updatePasskey(id, friendlyName) {
    const response = await $axios.patch(
      `${identityBaseUrl}/identity/passkey/${id}`,
      { friendlyName },
    )
    const updated = response.data.data
    const index = passkeys.value.findIndex((p) => p.id === id)
    if (index !== -1) {
      passkeys.value[index] = { ...passkeys.value[index], ...updated }
    }
    return updated
  }

  async function deletePasskey(id) {
    await $axios.delete(`${identityBaseUrl}/identity/passkey/${id}`)
    passkeys.value = passkeys.value.filter((p) => p.id !== id)
  }

  function _shutdownIntercom() {
    if (env('intercomChat') === 'true' && window?.Intercom) {
      window.Intercom('shutdown')
    }
  }

  async function _clearCookies() {
    // [IDENT] TO DO: clear all cookies, not just specific ones
    const path = rootStore.isEmbedded ? `${nuxtBaseUrl}/embedded` : `${nuxtBaseUrl}/`
    await $lendioCookies.remove('accessToken', { path })
    await $lendioCookies.remove('token', { path })
    await $lendioCookies.remove('anonymousId', { path })
    await $lendioCookies.remove('borrowerId', { path })
    await $lendioCookies.remove('intercom_hash', { path })
  }

  function _clearLocalStorage() {
    const keysToPreserve = [
      /^dreams/,
      /^rememberedIdentity/,
      /^latestOptionsHash/,
      /^virtualProductState/,
      /^passkeySkipCount$/,
    ]

    const preservedItems = [...Array(localStorage.length)]
      .map((_, i) => localStorage.key(i))
      .filter((key) => keysToPreserve.some((pattern) => pattern.test(key)))
      .reduce((collect, key) => {
        return Object.assign(collect, { [key]: localStorageService.getItem(key) })
      }, {})
    localStorageService.clear()
    Object.keys(preservedItems).forEach((key) => localStorageService.setItem(key, preservedItems[key]))
  }

  function _shutdownPusher() {
    if (typeof window !== 'undefined' && window.lendioPusher) {
      try {
        window.lendioPusher.disconnect()
        delete window.lendioPusher
      } catch (e) {
        log.error('Failed to disconnect Pusher', e)
      }
    }
  }

  return {
    // state
    businessSelectUrl,
    passkeys,
    ssoLoginUrl,

    // getters
    externallyManagedIdentity,
    mustSetPassword,
    onlySsoSupported,
    postLoginIdentityUrls,

    //actions
    checkForExistingEmail,
    checkForSetPassword,
    getAuthUserIdP,
    getPasskeys,
    getPasskeyRegisterOptions,
    getSupportedIdentityProvidersForClient,
    initiateSamlSsoLogin,
    isIframeOnly,
    logout,
    tokenLogin,
    consumeWidgetToken,
    deletePasskey,
    updatePasskey,
    updatePassword,
    verifyPasskeyRegistration,
  };
});
